import { useState, useEffect, useMemo } from 'react';
import { Formik } from 'formik';
import {
  Box,
  Typography,
  Button,
  MenuItem,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { 
  AddCircleOutline, 
  ContentCopy, 
  Edit 
} from '@mui/icons-material';
import { 
  useGetAirportsQuery, 
  useGetActiveAcTypesQuery 
} from 'features/apiSlice';
import api from 'state/axios';
import { flightSchema, toMin, autoColon, STATUS_OPTIONS } from './constants';
import DayToggle from './DayToggle';


const FlightFormDialog = ({ 
  open, 
  onClose, 
  data, 
  initialValues, 
  seasons, 
  onSave,
}) => {
  const { data: airportsData } = useGetAirportsQuery(undefined, { skip: !open });
  const { data: acTypesData }  = useGetActiveAcTypesQuery(undefined, { skip: !open });
  const [serviceTypes, setServiceTypes] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get('/data/servicetypes')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
        setServiceTypes(list);
      })
      .catch(() => setServiceTypes([]));
  }, [open]);

  const airportCodes = useMemo(
    () => (airportsData ?? []).map((a) => a.iataCode).filter(Boolean).sort(),
    [airportsData],
  );
  const airportMap = useMemo(
    () => Object.fromEntries(
      (airportsData ?? []).filter((a) => a.iataCode).map((a) => [a.iataCode, a])
    ),
    [airportsData],
  );
  const acTypes = useMemo(() => acTypesData ?? [], [acTypesData]);

  const titleIcon = data?._id
    ? <Edit fontSize="small" />
    : data
      ? <ContentCopy fontSize="small" />
      : <AddCircleOutline fontSize="small" />;

  const titleText = data?._id
    ? `${data.carrier}${data.flightNo} · ${data.dep}→${data.arr}`
    : data
      ? 'Duplicate Flight'
      : 'New Flight';

  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box display="flex" alignItems="center" gap={1}>
          {titleIcon} {titleText}
        </Box>
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={flightSchema}
        onSubmit={onSave}
        enableReinitialize
      >
        {({ values, errors, touched, handleSubmit, isSubmitting, setFieldValue, handleBlur }) => {
          const upper  = (name) => (e) => setFieldValue(name, e.target.value.toUpperCase());
          const stdM   = toMin(values.std);
          const staM   = toMin(values.sta);
          const blkMin = (values.std && values.sta) ? ((staM - stdM + 1440) % 1440) : 0;
          const blkTxt = blkMin > 0
            ? `${Math.floor(blkMin / 60)}h${String(blkMin % 60).padStart(2, '0')}m`
            : '-';

          const makeAC = (name) => ({
            freeSolo: true,
            size: 'small',
            options: airportCodes,
            value: values[name] || null,
            inputValue: values[name] || '',
            onChange: (_, v) => setFieldValue(name, (v || '').toUpperCase().slice(0, 3)),
            onInputChange: (_, v, reason) => {
              if (reason === 'input') setFieldValue(name, v.toUpperCase().slice(0, 3));
            },
            renderOption: (props, option) => {
              const ap = airportMap[option];
              return (
                <li {...props} key={option}>
                  <Box>
                    <Typography variant="body2" fontFamily="monospace" fontWeight="bold">{option}</Typography>
                    {(ap?.airportName || ap?.city) && (
                      <Typography variant="caption" color="text.secondary">
                        {ap.airportName || ap.city}
                      </Typography>
                    )}
                  </Box>
                </li>
              );
            },
          });

          return (
            <form onSubmit={handleSubmit}>
              <DialogContent dividers sx={{ py: 1.5 }}>
                <Box 
                  display="flex"
                  flexDirection="column"
                  gap={2}
                >
                  <TextField
                    select 
                    fullWidth 
                    size="small" 
                    label="Season *" 
                    name="seasonId"
                    value={values.seasonId}
                    onChange={(e) => setFieldValue('seasonId', e.target.value)}
                    error={touched.seasonId && Boolean(errors.seasonId)}
                    helperText={touched.seasonId && errors.seasonId}
                  >
                    {seasons.map((s) => (
                      <MenuItem key={s._id} value={s._id}>{s.seasonCode} - {s.seasonName}</MenuItem>
                    ))}
                  </TextField>

                  <Box 
                    sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(12, 1fr)', 
                      alignItems: 'flex-start',
                      gap: 1, 
                    }}
                  >
                    <TextField
                      fullWidth 
                      size="small" 
                      label="Carrier *" 
                      name="carrier"
                      value={values.carrier} onChange={upper('carrier')} 
                      onBlur={handleBlur}
                      autoFocus={!data?._id}
                      slotProps={{ htmlInput: { maxLength: 3 } }}
                      sx={{ gridColumn: 'span 2', '& input': { textTransform: 'uppercase' } }}
                    />

                    <TextField
                      fullWidth 
                      size="small" 
                      label="Flt No *" 
                      name="flightNo"
                      value={values.flightNo}
                      onChange={(e) => setFieldValue('flightNo', e.target.value)} 
                      onBlur={handleBlur}
                      slotProps={{ htmlInput: { maxLength: 5 } }}
                      sx={{ gridColumn: 'span 2' }}
                    />

                    <Autocomplete
                      {...makeAC('dep')}
                      renderInput={(p) => (
                        <TextField 
                          {...p} 
                          fullWidth 
                          label="DEP *" 
                          name="dep" 
                          onBlur={handleBlur}
                        />
                      )}
                      sx={{ gridColumn: 'span 2' }}
                    />
                    
                    <Autocomplete
                      {...makeAC('arr')}
                      renderInput={(p) => (
                        <TextField 
                          {...p} 
                          fullWidth 
                          label="ARR *" 
                          name="arr" 
                          onBlur={handleBlur}
                        />
                      )}
                      sx={{ gridColumn: 'span 2' }}
                    />
                    
                    <TextField
                      fullWidth 
                      size="small" 
                      select 
                      label="AC Type" 
                      name="acType"
                      value={values.acType || ''}
                      onChange={(e) => setFieldValue('acType', e.target.value)}
                      sx={{ gridColumn: 'span 2' }}
                    >
                      <MenuItem value="">None</MenuItem>
                      {acTypes.map((t) => (
                        <MenuItem key={t._id} value={t.acTypeCode}>
                          {t.acTypeCode}
                        </MenuItem>
                      ))}
                    </TextField>
                    
                    <TextField
                      fullWidth 
                      size="small" 
                      select 
                      label="Svc" 
                      name="serviceType"
                      value={values.serviceType || ''}
                      onChange={(e) => setFieldValue('serviceType', e.target.value)}
                      sx={{ gridColumn: 'span 2' }}
                    >
                      <MenuItem value="">None</MenuItem>
                      {serviceTypes.map((s) => (
                        <MenuItem key={s._id} value={s.code}>
                          {s.code }
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  <Box 
                    sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(12, 1fr)', 
                      alignItems: 'flex-start', 
                      gap: 1, 
                    }}
                  >
                    <TextField
                      fullWidth 
                      size="small" 
                      label="STD *" 
                      name="std"
                      value={values.std}
                      onChange={(e) => setFieldValue('std', autoColon(e.target.value))}
                      onBlur={handleBlur}
                      slotProps={{ htmlInput: { maxLength: 5 } }}
                      sx={{ gridColumn: 'span 2', '& input': { textAlign: 'center' } }}
                    />

                    <Box 
                      sx={{ 
                        gridColumn: 'span 2', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: 30, 
                        gap: 0.2 
                      }}
                    >
                      <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: blkMin > 0 ? 'primary.main' : 'text.disabled', fontWeight: 'bold' }}>
                        {blkTxt}
                      </Typography>
                      <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', fontFamily: 'monospace' }}>BLK</Typography>
                    </Box>
                    
                    <TextField
                      fullWidth size="small" 
                      label="STA *" 
                      name="sta" 
                      value={values.sta}
                      onChange={(e) => setFieldValue('sta', autoColon(e.target.value))}
                      onBlur={handleBlur}
                      slotProps={{ htmlInput: { maxLength: 5 } }}
                      sx={{ gridColumn: 'span 2', '& input': { textAlign: 'center' } }}
                    />                   
                    
                    <TextField
                      fullWidth 
                      size="small" 
                      select 
                      label="Status" 
                      name="status"
                      value={values.status || 'planned'}
                      onChange={(e) => setFieldValue('status', e.target.value)}
                      sx={{ gridColumn: 'span 6' }}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </TextField>                   
                  </Box>

                  {/* Days */}
                  <Box>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      display="block" 
                      mb={1}
                    >
                      Days of Operation *
                    </Typography>
                    <DayToggle
                      value={values.daysOfOperation}
                      onChange={(v) => setFieldValue('daysOfOperation', v)}
                    />
                    {touched.daysOfOperation && errors.daysOfOperation && (
                      <Typography variant="caption" color="error">
                        {errors.daysOfOperation}
                      </Typography>
                    )}
                  </Box>

                  {/* Effective dates: span 6 + span 6 */}
                  <Box 
                    sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(12, 1fr)', 
                      gap: 1, 
                      mt: 1,
                    }}
                  >
                    <TextField
                      fullWidth 
                      size="small" 
                      label="Effective From" 
                      name="effectiveFrom" 
                      type="date"
                      value={values.effectiveFrom || ''}
                      onChange={(e) => setFieldValue('effectiveFrom', e.target.value)}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ gridColumn: 'span 6' }}
                    />
                      <TextField
                        fullWidth 
                        size="small" 
                        label="Effective To" 
                        name="effectiveTo" 
                        type="date"
                        value={values.effectiveTo || ''}
                        onChange={(e) => setFieldValue('effectiveTo', e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ gridColumn: 'span 6' }}
                      />
                  </Box>

                  {/* Flags */}
                  <Box display="flex" gap={3} mb={0.5}>
                    <FormControlLabel
                      control={
                        <Switch size="small" checked={!!values.codeshare}
                          onChange={(e) => setFieldValue('codeshare', e.target.checked)} />
                      }
                      label={<Typography variant="body2">Codeshare</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Switch size="small" checked={!!values.slotCoordinated}
                          onChange={(e) => setFieldValue('slotCoordinated', e.target.checked)} />
                      }
                      label={<Typography variant="body2">Slot Coordinated</Typography>}
                    />
                  </Box>

                  {/* Notes */}
                  <TextField
                    fullWidth size="small" multiline rows={2} label="Notes" name="notes"
                    value={values.notes || ''}
                    onChange={(e) => setFieldValue('notes', e.target.value)}
                  />
                </Box>
              </DialogContent>

              <DialogActions>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="outlined" 
                  disabled={isSubmitting}
                >
                  {data?._id ? 'Update' : 'Create'}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default FlightFormDialog;
